import React, { useState, useRef, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';

/**
 * A reusable Tooltip component using React Portals for overflow safety.
 * Renders into document.body to avoid clipping by parent container overflow or z-index issues.
 * On mobile screens (< 768px), tooltips automatically render at the horizontal center of the
 * viewport with safe padding constraints to prevent being trimmed off screen.
 */
export interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  disabled?: boolean;
  className?: string;
  delay?: number;
  tooltipStyle?: React.CSSProperties;
  gap?: number;
}

export function Tooltip({
  content,
  children,
  position = 'top',
  disabled = false,
  className = '',
  delay = 200,
  tooltipStyle = {},
  gap = 8
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState<{
    top: number;
    left: number;
    mobilePlacement: 'top' | 'bottom';
  }>({ top: 0, left: 0, mobilePlacement: 'bottom' });
  const [supportsHover, setSupportsHover] = useState(true);
  const [isMobile, setIsMobile] = useState(() => {
    return typeof window !== 'undefined' ? window.innerWidth < 768 : false;
  });

  const triggerRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tooltipId = useId();

  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(hover: hover)');
      setSupportsHover(mediaQuery.matches);

      const handleChange = (e: MediaQueryListEvent) => {
        setSupportsHover(e.matches);
      };

      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
      } else {
        (mediaQuery as any).addListener?.(handleChange);
        return () => (mediaQuery as any).removeListener?.(handleChange);
      }
    }
  }, []);

  // Track responsive screen size changes (e.g. mobile rotation or resize)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  /**
   * Calculates the absolute coordinates for the tooltip based on the trigger's position.
   */
  const calculateCoords = React.useCallback(() => {
    if (!triggerRef.current) return { top: 0, left: 0, mobilePlacement: 'bottom' as const };

    const rect = triggerRef.current.getBoundingClientRect();
    const isMobileScreen = typeof window !== 'undefined' ? window.innerWidth < 768 : false;

    if (isMobileScreen) {
      // On mobile screens, horizontally center the tooltip in the viewport to avoid trimming
      const left = typeof window !== 'undefined' ? window.innerWidth / 2 : 0;

      // Determine vertical placement: place above if space below is tight and above has more room
      const spaceBelow = typeof window !== 'undefined' ? window.innerHeight - rect.bottom : 200;
      const spaceAbove = rect.top;
      const placeTop = spaceBelow < 140 && spaceAbove > spaceBelow;

      let top = placeTop ? rect.top - gap : rect.bottom + gap;
      if (typeof window !== 'undefined') {
        top = Math.max(16, Math.min(top, window.innerHeight - 16));
      }

      return {
        top,
        left,
        mobilePlacement: placeTop ? ('top' as const) : ('bottom' as const),
      };
    }

    let top = 0;
    let left = 0;

    switch (position) {
      case 'top':
        top = rect.top - gap;
        left = rect.left + rect.width / 2;
        break;
      case 'bottom':
        top = rect.bottom + gap;
        left = rect.left + rect.width / 2;
        break;
      case 'left':
        top = rect.top + rect.height / 2;
        left = rect.left - gap;
        break;
      case 'right':
        top = rect.top + rect.height / 2;
        left = rect.right + gap;
        break;
      default:
        top = rect.bottom + gap;
        left = rect.left + rect.width / 2;
    }

    return { top, left, mobilePlacement: 'bottom' as const };
  }, [position, gap]);

  const updatePosition = React.useCallback(() => {
    if (isVisible) {
      setCoords(calculateCoords());
    }
  }, [isVisible, calculateCoords]);

  // Handle dynamic repositioning on resize or scroll
  useEffect(() => {
    if (isVisible) {
      requestAnimationFrame(updatePosition);
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
    }

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isVisible, updatePosition]);

  // Click/tap outside handler for both desktop and mobile
  useEffect(() => {
    if (!isVisible) return;

    const handleOutsideClick = (e: MouseEvent | TouchEvent) => {
      const tooltipEl = document.getElementById(tooltipId);
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node) &&
        (!tooltipEl || !tooltipEl.contains(e.target as Node))
      ) {
        setIsVisible(false);
      }
    };

    const handleMobileScroll = (e: Event) => {
      if (!supportsHover || isMobile) {
        const tooltipEl = document.getElementById(tooltipId);
        if (tooltipEl && tooltipEl.contains(e.target as Node)) {
          return; // Ignore scroll events inside the tooltip itself
        }
        setIsVisible(false);
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener('click', handleOutsideClick);
      document.addEventListener('touchend', handleOutsideClick);
    }, 50);

    window.addEventListener('scroll', handleMobileScroll, true);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleOutsideClick);
      document.removeEventListener('touchend', handleOutsideClick);
      window.removeEventListener('scroll', handleMobileScroll, true);
    };
  }, [isVisible, supportsHover, isMobile, tooltipId]);

  // Ensure tooltip hides if it becomes disabled while visible
  useEffect(() => {
    if (disabled && isVisible) {
      requestAnimationFrame(() => setIsVisible(false));
      if (timerRef.current) clearTimeout(timerRef.current);
    }
  }, [disabled, isVisible]);

  const showTooltip = () => {
    if (disabled) return;
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      setCoords(calculateCoords());
      setIsVisible(true);
    }, delay);
  };

  const hideTooltip = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);

    closeTimerRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 150);
  };

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const handleTriggerClick = (e: React.MouseEvent) => {
    if (disabled) return;

    const anchor = (e.target as HTMLElement).closest('a');
    if (anchor) {
      e.preventDefault();
    }

    // Blur the clicked element to avoid keeping focus and showing focus tooltips
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.blur();
    }
    if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    if (!isMobile && supportsHover) {
      hideTooltip();
    } else {
      if (isVisible) {
        if (timerRef.current) clearTimeout(timerRef.current);
        if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
        setIsVisible(false);
      } else {
        setCoords(calculateCoords());
        setIsVisible(true);
      }
    }
  };

  const getTransform = () => {
    if (isMobile) {
      return coords.mobilePlacement === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)';
    }

    switch (position) {
      case 'top': return 'translate(-50%, -100%)';
      case 'bottom': return 'translate(-50%, 0)';
      case 'left': return 'translate(-100%, -50%)';
      case 'right': return 'translate(0, -50%)';
      default: return 'translate(-50%, 0)';
    }
  };

  const hoverHandlers = (!isMobile && supportsHover) ? {
    onMouseEnter: showTooltip,
    onMouseLeave: hideTooltip,
    onFocus: showTooltip,
    onBlur: hideTooltip,
    onClick: handleTriggerClick,
  } : {
    onClick: handleTriggerClick,
  };

  return (
    <>
      <div
        ref={triggerRef}
        {...hoverHandlers}
        className={`inline-block ${className}`}
      >
        {children}
      </div>

      {isVisible && createPortal(
        <div
          id={tooltipId}
          onMouseEnter={(!isMobile && supportsHover) ? cancelClose : undefined}
          onMouseLeave={(!isMobile && supportsHover) ? hideTooltip : undefined}
          className="fixed z-[10000] px-2.5 py-1.5 text-[11px] font-semibold bg-popover text-popover-foreground border border-border rounded-md shadow-lg pointer-events-auto"
          style={{
            top: coords.top,
            left: isMobile ? '50%' : coords.left,
            transform: getTransform(),
            transition: 'none',
            animation: 'none',
            maxWidth: isMobile ? 'calc(100vw - 32px)' : '200px',
            whiteSpace: isMobile ? 'normal' : 'nowrap',
            textAlign: 'center',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            pointerEvents: 'auto',
            boxSizing: 'border-box',
            ...tooltipStyle,
            // Enforce viewport containment on mobile to strictly prevent edge clipping
            ...(isMobile ? {
              left: '50%',
              maxWidth: 'calc(100vw - 32px)',
              width: 'max-content',
              maxHeight: 'calc(100vh - 32px)',
              overflowY: 'auto',
              boxSizing: 'border-box',
            } : {}),
          }}
        >
          {content}
        </div>,
        document.body
      )}
    </>
  );
}
